import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { fromEvent, interval, Subject } from 'rxjs';
import { debounce, takeUntil, tap } from 'rxjs/operators';

export type TooltipPosition = 'top' | 'right' | 'top center' | 'left';

@Component({
  selector: 'app-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss']
})
export class TooltipComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() id: string;
  @Input() ariaLabel: string;
  @Input() icon = 'question_tooltip.svg';
  @Input() position: TooltipPosition;
  @Input() width: string;
  private originalPosition: TooltipPosition;

  @ViewChild('tooltip') tooltip: ElementRef<HTMLDivElement>;
  @ViewChild('tooltipIcon') tooltipIcon: ElementRef<HTMLImageElement>;

  positions = {
    top: '-1.25rem',
    right: '1.25rem'
  };

  ngUnsubscribe = new Subject<void>();

  constructor(private host: ElementRef) {
    if(this.host.nativeElement.attributes.getNamedItem('id') !== null) {
      this.host.nativeElement.attributes.removeNamedItem('id');
    }
  }

  ngOnInit(): void {
    fromEvent(window, 'resize').pipe(
      takeUntil(this.ngUnsubscribe),
      debounce(() => interval(500)),
      tap(() => this.calculatePosition())
    ).subscribe();
  }

  ngAfterViewInit(): void {
    this.width = this.width || '20rem';
    this.tooltip.nativeElement.style.setProperty('width', this.width);
    this.tooltip.nativeElement.style.setProperty('min-width', this.width);
    this.tooltip.nativeElement.style.setProperty('z-index', '100');

    this.detachTooltip();
    this.calculatePosition();
    this.tooltip.nativeElement.style.top = '0';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.position?.currentValue && !this.originalPosition) {
      this.originalPosition = changes.position.currentValue;
    }
    
    if(!this.originalPosition) {
      this.originalPosition = 'top';
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.tooltip?.nativeElement.remove();
  }

  calculatePosition(): void {
    const icon = this.tooltipIcon.nativeElement.getBoundingClientRect();
    const windowPadding = 10;
    const rect = this.tooltip.nativeElement.getBoundingClientRect();
    const isCentered = this.position.includes('center');
    const calculatedTooltipWidth = rect.width / (isCentered ? 2 : 1);
    const tooltipArrow = 10;
    const offset = Math.max(0, (icon.left + calculatedTooltipWidth + tooltipArrow) - (window.innerWidth - windowPadding));
    
    this.tooltip.nativeElement.style.top = `${window.scrollY + icon.top + 10}px`;

    if(offset > 0) {
      this.tooltip.nativeElement.style.left = `${icon.left + 10 - (offset / 2)}px`;
      this.tooltip.nativeElement.style.setProperty('--before-position', `calc(${offset}px + ${rect.width}px / 3)`);
    } else {
      this.tooltip.nativeElement.style.left = `${icon.left + 10}px`;
    }
  }

  private detachTooltip(): void {
    document.body.append(this.tooltip.nativeElement);
  }

  protected showTooltip(): void {
    this.tooltip.nativeElement.style.visibility = 'visible';
    this.tooltip.nativeElement.style.opacity = '1';
    this.calculatePosition();
  }

  protected hideTooltip(): void {
    this.tooltip.nativeElement.style.visibility = 'hidden';
    this.tooltip.nativeElement.style.opacity = '0';
  }
}
